import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAllCustomers } from '../services/customer_service'
import { generateAccountingReport } from '../services/accounting_report_service'
import './PublishingReport.css'

const reportTypes = [
  {
    value: 'summary',
    label: 'Summary',
    description: 'Key financial totals for review and printing.',
  },
  {
    value: 'list',
    label: 'List',
    description: 'Detailed transactions downloaded as an Excel file.',
  },
]

const periods = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This week' },
  { value: 'last-week', label: 'Last week' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'this-year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
]

const entities = [
  {
    value: 'revenue',
    label: 'Revenue',
    hint: 'Sales, discounts, collections, and balances',
  },
  {
    value: 'payments',
    label: 'Payments received',
    hint: 'Receipts by customer and payment method',
  },
  {
    value: 'receivables',
    label: 'Receivables',
    hint: 'Outstanding invoices and debtor balances',
  },
  {
    value: 'old-payments',
    label: 'Legacy receipts',
    hint: 'Payments recorded outside current orders',
  },
  {
    value: 'royalties',
    label: 'Royalties',
    hint: 'Computed author royalties and payment status',
  },
]

const orderTypes = [
  { value: 'all', label: 'All order types' },
  { value: 'published_book', label: 'Published books' },
  { value: 'exercise_book', label: 'Exercise books' },
]

const paymentMethods = [
  { value: 'all', label: 'All payment methods' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Mobile Money', label: 'Mobile Money' },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Bank Transfer', label: 'Bank transfer' },
]

const royaltyStatuses = [
  { value: 'all', label: 'All royalty statuses' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Unpaid', label: 'Unpaid' },
]

const getOptionValue = (option) =>
  option?.customerid || option?.customer_id || option?.id || option?.value
const getOptionLabel = (option) =>
  option?.cust_name || option?.customer_name || option?.name || option?.label
const formatFieldLabel = (value) =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
const formatReportValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function SummaryData({ data }) {
  return (
    <dl className='generated-report__metrics'>
      {Object.entries(data || {}).map(([key, value]) => (
        <div key={key}>
          <dt>{formatFieldLabel(key)}</dt>
          <dd>{formatReportValue(value)}</dd>
        </div>
      ))}
    </dl>
  )
}

function AccountingReport() {
  const [reportType, setReportType] = useState('summary')
  const [period, setPeriod] = useState('this-month')
  const [entity, setEntity] = useState('revenue')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [orderType, setOrderType] = useState('all')
  const [paymentMethod, setPaymentMethod] = useState('all')
  const [customer, setCustomer] = useState('all')
  const [royaltyStatus, setRoyaltyStatus] = useState('all')
  const [customers, setCustomers] = useState([])
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedReport, setGeneratedReport] = useState(null)

  useEffect(() => {
    let isMounted = true
    getAllCustomers()
      .then((items) => {
        if (isMounted) setCustomers(items)
      })
      .catch((requestError) => {
        if (isMounted) setError(requestError.message)
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [])

  const selectedEntity = entities.find((item) => item.value === entity)
  const selectedPeriod = periods.find((item) => item.value === period)
  const selectedType = reportTypes.find((item) => item.value === reportType)
  const selectedCustomer = customers.find(
    (item) => String(getOptionValue(item)) === customer,
  )
  const selectedOrderType = orderTypes.find((item) => item.value === orderType)
  const selectedPaymentMethod = paymentMethods.find(
    (item) => item.value === paymentMethod,
  )
  const selectedRoyaltyStatus = royaltyStatuses.find(
    (item) => item.value === royaltyStatus,
  )
  const usesPaymentMethod = entity === 'payments' || entity === 'old-payments'
  const usesOrderType = entity !== 'royalties'
  const usesCustomer = entity !== 'royalties'
  const scope =
    entity === 'royalties'
      ? selectedRoyaltyStatus.label
      : [
          selectedOrderType.label,
          selectedCustomer ? getOptionLabel(selectedCustomer) : 'All customers',
          ...(usesPaymentMethod ? [selectedPaymentMethod.label] : []),
        ].join(' · ')

  async function submit(event) {
    event.preventDefault()
    setError('')
    setSuccess('')
    setGeneratedReport(null)
    if (period === 'custom' && (!fromDate || !toDate)) {
      setError('Choose both custom period dates.')
      return
    }
    if (fromDate && toDate && fromDate > toDate) {
      setError('The start date cannot be after the end date.')
      return
    }

    setIsGenerating(true)
    try {
      const data = await generateAccountingReport({
        report_type: reportType,
        period,
        ...(period === 'custom'
          ? { from_date: fromDate, to_date: toDate }
          : {}),
        entity,
        order_type: usesOrderType ? orderType : 'all',
        customer_id: usesCustomer ? customer : 'all',
        payment_method: usesPaymentMethod ? paymentMethod : 'all',
        royalty_status: entity === 'royalties' ? royaltyStatus : 'all',
      })
      if (reportType === 'list') {
        setSuccess('The Excel accounting report has been downloaded.')
      } else {
        setGeneratedReport({
          data,
          entity: selectedEntity.label,
          reportType: selectedType.label,
          period:
            period === 'custom'
              ? `${fromDate} to ${toDate}`
              : selectedPeriod.label,
          scope,
          generatedAt: new Date().toLocaleString(),
        })
      }
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsGenerating(false)
    }
  }

  function chooseEntity(value) {
    setEntity(value)
    setError('')
    setSuccess('')
    setGeneratedReport(null)
  }

  return (
    <div className='publishing-report-page'>
      <nav className='publishing-report__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <span>Reports</span>
        <span aria-hidden='true'>/</span>
        <strong>Accounting</strong>
      </nav>

      <header className='publishing-report__header'>
        <div>
          <span className='app-content__eyebrow'>Accounting reports</span>
          <h1>Build a financial report</h1>
          <p>Review revenue, cash collection, receivables, and liabilities.</p>
        </div>
        <div className='publishing-report__mark' aria-hidden='true'>
          <span>LEDGER</span>
          <strong>02</strong>
        </div>
      </header>

      <form className='report-builder' onSubmit={submit}>
        <section
          className='report-builder__section'
          aria-labelledby='accounting-type-title'
        >
          <div className='report-builder__section-heading'>
            <span>01</span>
            <div>
              <h2 id='accounting-type-title'>Report type</h2>
              <p>Choose a printable summary or detailed Excel ledger.</p>
            </div>
          </div>
          <div className='report-type-options'>
            {reportTypes.map((item) => (
              <label
                className={`report-type-option ${reportType === item.value ? 'is-selected' : ''}`}
                key={item.value}
              >
                <input
                  type='radio'
                  name='reportType'
                  value={item.value}
                  checked={reportType === item.value}
                  onChange={(event) => setReportType(event.target.value)}
                />
                <span
                  className='report-type-option__indicator'
                  aria-hidden='true'
                />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='accounting-period-title'
        >
          <div className='report-builder__section-heading'>
            <span>02</span>
            <div>
              <h2 id='accounting-period-title'>Period</h2>
              <p>Select the accounting period to recognize.</p>
            </div>
          </div>
          <div className='report-periods'>
            {periods.map((item) => (
              <button
                className={period === item.value ? 'is-selected' : ''}
                type='button'
                key={item.value}
                onClick={() => setPeriod(item.value)}
                aria-pressed={period === item.value}
              >
                {item.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className='report-custom-period'>
              <label>
                From
                <input
                  type='date'
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(event) => setFromDate(event.target.value)}
                  required
                />
              </label>
              <span aria-hidden='true'>to</span>
              <label>
                To
                <input
                  type='date'
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(event) => setToDate(event.target.value)}
                  required
                />
              </label>
            </div>
          )}
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='accounting-entity-title'
        >
          <div className='report-builder__section-heading'>
            <span>03</span>
            <div>
              <h2 id='accounting-entity-title'>Financial area</h2>
              <p>Select the ledger or financial position to report.</p>
            </div>
          </div>
          <div className='report-entities'>
            {entities.map((item) => (
              <label
                className={`report-entity ${entity === item.value ? 'is-selected' : ''}`}
                key={item.value}
              >
                <input
                  type='radio'
                  name='entity'
                  value={item.value}
                  checked={entity === item.value}
                  onChange={(event) => chooseEntity(event.target.value)}
                />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.hint}</small>
                </span>
                <span className='report-entity__check' aria-hidden='true'>
                  ✓
                </span>
              </label>
            ))}
          </div>
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='accounting-options-title'
        >
          <div className='report-builder__section-heading'>
            <span>04</span>
            <div>
              <h2 id='accounting-options-title'>Report options</h2>
              <p>Narrow the ledger or include all available records.</p>
            </div>
          </div>
          <div className='report-entity-options'>
            {usesOrderType && (
              <label>
                Order type
                <select
                  value={orderType}
                  onChange={(event) => setOrderType(event.target.value)}
                >
                  {orderTypes.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {usesCustomer && (
              <label>
                Customer
                <select
                  value={customer}
                  onChange={(event) => setCustomer(event.target.value)}
                  disabled={optionsLoading}
                >
                  <option value='all'>
                    {optionsLoading ? 'Loading customers...' : 'All customers'}
                  </option>
                  {customers.map((item) => (
                    <option
                      key={getOptionValue(item)}
                      value={getOptionValue(item)}
                    >
                      {getOptionLabel(item)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {usesPaymentMethod && (
              <label>
                Payment method
                <select
                  value={paymentMethod}
                  onChange={(event) => setPaymentMethod(event.target.value)}
                >
                  {paymentMethods.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {entity === 'royalties' && (
              <label>
                Royalty status
                <select
                  value={royaltyStatus}
                  onChange={(event) => setRoyaltyStatus(event.target.value)}
                >
                  {royaltyStatuses.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </section>

        {error && (
          <div className='report-builder__error' role='alert'>
            {error}
          </div>
        )}
        {success && (
          <div className='report-builder__success' role='status'>
            {success}
          </div>
        )}
        <footer className='report-builder__footer'>
          <div>
            <span>Ready to generate</span>
            <strong>
              {selectedType.label} · {selectedPeriod.label} ·{' '}
              {selectedEntity.label} · {scope}
            </strong>
          </div>
          <button type='submit' disabled={isGenerating}>
            {isGenerating
              ? 'Generating...'
              : reportType === 'list'
                ? 'Generate & download'
                : 'Generate report'}{' '}
            {!isGenerating && <span aria-hidden='true'>→</span>}
          </button>
        </footer>
      </form>

      {generatedReport && (
        <section className='generated-report' aria-live='polite'>
          <div>
            <span className='app-content__eyebrow'>Financial report ready</span>
            <h2>
              {generatedReport.entity} {generatedReport.reportType}
            </h2>
            <p>
              {generatedReport.scope} · {generatedReport.period} · Generated{' '}
              {generatedReport.generatedAt}
            </p>
            <SummaryData data={generatedReport.data} />
          </div>
          <button type='button' onClick={() => window.print()}>
            Print report
          </button>
        </section>
      )}
    </div>
  )
}

export default AccountingReport
